/**
 * Colours for one thing ([D-041](../../../docs/DECISIONS.md#d-041),
 * [D-138](../../../docs/DECISIONS.md#d-138), mockup 37).
 *
 * Twelve colours, bought once per item and owned forever. Swapping between the
 * ones you own is free, and going back to the item's own colour always is.
 * Every choice is previewed on the child before anything is spent, so nobody
 * buys a colour to find out what it looks like.
 *
 * The drawing is local, generated markup keyed by id, exactly as in the
 * wardrobe: the server sends which colours are owned, never what to draw.
 */

import '@capytalk/tokens/blocks/dye.css';
import { DYES, type Dye, avatarSvg } from '@capytalk/tokens';
import { useCallback, useEffect, useState } from 'react';
import type { Client } from './api.js';
import Sneakers from './Sneakers.js';

type Shelf = NonNullable<Awaited<ReturnType<Client['shop']['dyes']['query']>>>;
type Worn = Record<string, { itemId: string; dye: string | null } | null>;

const title = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

export default function DyeScreen({
  client,
  slot,
  itemId,
  name,
  worn,
  look,
  onDone,
}: {
  readonly client: Client;
  readonly slot: string;
  readonly itemId: string;
  readonly name: string;
  /** What the child has on now, so the preview is them and not a mannequin. */
  readonly worn: Worn;
  readonly look: { readonly species: string; readonly background: string };
  /** With the outfit the server answered with, when something was put on. */
  readonly onDone: (worn: Worn | null) => void;
}) {
  const [shelf, setShelf] = useState<Shelf | null>(null);
  const [trouble, setTrouble] = useState(false);
  const [picked, setPicked] = useState<string | null>(worn[slot]?.dye ?? null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setTrouble(false);
    Promise.resolve()
      .then(() => client.shop.dyes.query({ itemId }))
      .then((found) => {
        if (found === null) setTrouble(true);
        else setShelf(found);
      })
      .catch(() => {
        setTrouble(true);
      });
  }, [client, itemId]);

  useEffect(load, [load]);

  const wearIt = (dye: string | null) => {
    setBusy(true);
    Promise.resolve()
      .then(() => client.shop.wear.mutate({ slot, itemId, dye }))
      .then((next) => {
        onDone(next.worn ?? null);
      })
      .catch(() => {
        setTrouble(true);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const buyIt = (dye: string) => {
    setBusy(true);
    Promise.resolve()
      .then(() => client.shop.buyDye.mutate({ itemId, dye }))
      .then((answer) => {
        if (answer.refused === null) {
          // Bought is owned: put it straight on, which is why it was bought.
          wearIt(dye);
          return;
        }
        setBusy(false);
        load();
      })
      .catch(() => {
        setBusy(false);
        setTrouble(true);
      });
  };

  if (trouble) {
    return (
      <div className="grow pad center">
        <p className="sub" role="alert">
          The colours would not load. Please try again.
        </p>
        <button type="button" className="btn" style={{ marginTop: 12 }} onClick={load}>
          Try again
        </button>
        <button
          type="button"
          className="btn ghost"
          style={{ marginTop: 10 }}
          onClick={() => {
            onDone(null);
          }}
        >
          Back
        </button>
      </div>
    );
  }

  if (shelf === null) {
    return (
      <div className="grow pad center">
        <p className="sub">One moment…</p>
      </div>
    );
  }

  const owns = (dye: string | null) =>
    dye === null || shelf.dyes.some((one) => one.id === dye && one.owned);
  const colour = picked !== null && picked in DYES ? (picked as Dye) : null;
  const preview: Worn = { ...worn, [slot]: { itemId, dye: colour } };
  const called = colour === null ? name : `${title(colour)} ${name}`;
  const affordable = shelf.acorns >= shelf.price;

  return (
    <div className="grow pad">
      <h2>{name}</h2>
      <Sneakers
        text="Pick a colour. You can change it back any time, for free."
        audioRef={null}
        size={46}
      />

      <div className="center card" style={{ padding: '10px 12px 12px', marginTop: 12 }}>
        <svg
          viewBox="0 0 160 240"
          width={118}
          height={177}
          role="img"
          aria-label={`You, wearing the ${called}`}
          // Local, generated, CI-checked markup — never anything a response chose.
          dangerouslySetInnerHTML={{
            __html: avatarSvg({
              species: look.species,
              background: look.background,
              loadout: preview,
              uid: 'dye',
            }),
          }}
        />
        <div className="tiny" style={{ fontWeight: 800, marginTop: 4 }}>
          {called}
        </div>
      </div>

      <div className="kicker" style={{ margin: '13px 0 8px' }}>
        COLOURS
      </div>
      <button
        type="button"
        className={picked === null ? 'chip on' : 'chip'}
        aria-pressed={picked === null}
        style={{ marginBottom: 12 }}
        onClick={() => {
          setPicked(null);
        }}
      >
        Its own colour · free
      </button>
      <div className="dyegrid" role="group" aria-label="Colours">
        {shelf.dyes.map((one) => (
          <button
            key={one.id}
            type="button"
            className={`dye${one.owned ? ' own' : ''}${picked === one.id ? ' on' : ''}`}
            style={{
              background: DYES[one.id as Dye],
              font: 'inherit',
              cursor: 'pointer',
            }}
            aria-pressed={picked === one.id}
            aria-label={
              one.owned
                ? `${one.id}, yours`
                : `${one.id}, ${String(shelf.price)} acorns`
            }
            onClick={() => {
              setPicked(one.id);
            }}
          >
            {!one.owned && (
              <span className="cost" aria-hidden="true">
                🌰 {shelf.price}
              </span>
            )}
            <span className="lbl" aria-hidden="true">
              {one.id}
            </span>
          </button>
        ))}
      </div>

      <div
        className="card"
        style={{ marginTop: 12, background: '#EDF6EA', borderColor: '#BFE0B6' }}
      >
        <div className="tiny" style={{ fontWeight: 700, color: '#3F8635' }}>
          A colour is yours forever once you buy it. Swapping between the ones you own
          is always free.
        </div>
      </div>

      {owns(picked) ? (
        <button
          type="button"
          className="btn"
          style={{ marginTop: 16 }}
          disabled={busy}
          onClick={() => {
            wearIt(picked);
          }}
        >
          {picked === null ? 'Wear its own colour' : `Wear ${picked}`}
        </button>
      ) : (
        <button
          type="button"
          className="btn"
          style={{ marginTop: 16 }}
          disabled={busy || !affordable || picked === null}
          onClick={() => {
            if (picked !== null) buyIt(picked);
          }}
        >
          {affordable
            ? `Buy ${String(picked)} for ${String(shelf.price)} 🌰`
            : `Not enough acorns yet — you have ${String(shelf.acorns)}`}
        </button>
      )}
      <button
        type="button"
        className="btn ghost"
        style={{ marginTop: 10 }}
        onClick={() => {
          onDone(null);
        }}
      >
        Back
      </button>
    </div>
  );
}
